import { useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { Navigate, useLocation } from "react-router-dom";
import { useViewAs } from "../contexts/ViewAsContext";
import { isPlatformAdmin } from "../utils/roles";
import { getAuthenticationRedirectUrl } from "../utils/redirectUtils";
import Icon from "./AppIcon";

const ProtectedAdminRoute = ({ children }) => {
  const { user, userProfile, loading: authLoading, profileLoading } = useAuth();
  const { isViewingAs, effectiveRole } = useViewAs();
  const location = useLocation();

  // Compute admin access synchronously — no useEffect delay
  const adminCheck = useMemo(() => {
    if (authLoading || profileLoading) return { loading: true };
    if (!user) return { hasAccess: false, error: "Authentication required" };
    if (isViewingAs && effectiveRole !== "admin") return { hasAccess: false };
    const hasAccess = isPlatformAdmin(user, userProfile);
    return { hasAccess, error: hasAccess ? null : "Admin privileges required" };
  }, [
    user,
    userProfile,
    authLoading,
    profileLoading,
    isViewingAs,
    effectiveRole,
  ]);

  // Still loading auth or profile
  if (adminCheck.loading) {
    return (
      <div className="min-h-screen bg-dm-page flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-dm-text-2">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    const currentPath = `${location?.pathname || "/"}${location?.search || ""}`;
    const authUrl = getAuthenticationRedirectUrl(currentPath);
    return <Navigate to={authUrl} replace />;
  }

  // When viewing as another role, silently redirect to home
  if (isViewingAs && !adminCheck.hasAccess) {
    return <Navigate to="/home" replace />;
  }

  // Access denied
  if (!adminCheck.hasAccess) {
    return (
      <div className="min-h-screen bg-dm-page flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={32} className="text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-dm-text-1 mb-2">
            Access Denied
          </h2>
          <p className="text-dm-text-2 mb-6">
            {adminCheck.error ||
              "You need admin privileges to access this page."}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.history?.back()}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Go Back
            </button>
            <div className="text-sm text-dm-text-2">
              If you believe this is an error, please contact support.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedAdminRoute;
